import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, getDoc, addDoc, deleteDoc, updateDoc, doc, serverTimestamp, orderBy, query, writeBatch, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Calendar, Plus, MapPin, Clock, Users, Trash2, Loader2,
  CalendarDays, CalendarCheck, History, Sparkles, ChevronRight,
  Bell, Star, Filter, HandCoins, Wallet, Pencil, CheckCircle, TrendingDown, ListChecks
} from 'lucide-react';
import { EventProgramDialog } from '../../components/EventProgramDialog';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../../components/ui/confirm-delete-dialog';
import { logAction } from '../../lib/audit';
import { fetchAllContributions, fetchUserContributions, isConfirmed } from '../../lib/contributions';
import { expectedAmountForMember, collectedTotal, expensesTotal, memberPaidForEvent } from '../../lib/events';

// Catégories Sass par défaut si non configurées (alignées sur la page Membres)
const FALLBACK_SASS = [
  { id: 'eleve', label: 'Élève', amount: 25000 },
  { id: 'etudiant', label: 'Étudiant(e)', amount: 35000 },
  { id: 'travailleur', label: 'Travailleur(se)', amount: 50000 }
];

const emptyFundraising = () => ({ enabled: false, budget: '', defaultAmount: '', amountsByCategory: {} });

// Convertit l'état du formulaire de collecte en objet stockable (montants -> nombres).
const cleanFundraising = (f) => {
  if (!f?.enabled) return { enabled: false };
  const amountsByCategory = {};
  Object.entries(f.amountsByCategory || {}).forEach(([k, v]) => {
    const n = parseInt(v, 10);
    if (!isNaN(n)) amountsByCategory[k] = n;
  });
  return {
    enabled: true,
    budget: f.budget ? parseInt(f.budget, 10) || 0 : null,
    defaultAmount: f.defaultAmount ? parseInt(f.defaultAmount, 10) || 0 : 0,
    amountsByCategory
  };
};

// Pré-remplit le formulaire de collecte depuis un événement existant.
const fundraisingToForm = (event) => {
  const f = event?.fundraising;
  if (!f?.enabled) return emptyFundraising();
  const amountsByCategory = {};
  Object.entries(f.amountsByCategory || {}).forEach(([k, v]) => { amountsByCategory[k] = String(v); });
  return {
    enabled: true,
    budget: f.budget != null ? String(f.budget) : '',
    defaultAmount: f.defaultAmount != null ? String(f.defaultAmount) : '',
    amountsByCategory
  };
};

// Découpe une date ISO en valeurs locales pour les inputs date/heure.
const isoToDateTime = (iso) => {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { date: '', time: '' };
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`
  };
};

// Champs principaux d'un événement (réutilisés en création et en édition).
function EventFormFields({ value, onChange, eventTypes }) {
  const setField = (k, v) => onChange({ ...value, [k]: v });
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="title">Titre</Label>
        <Input
          id="title"
          value={value.title}
          onChange={(e) => setField('title', e.target.value)}
          placeholder="Titre de l'événement"
          required
          data-testid="event-title-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="type">Type</Label>
        <div className="grid grid-cols-3 gap-2">
          {eventTypes.map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() => setField('type', type.value)}
              className={`p-3 rounded-xl border-2 text-center transition-all ${value.type === type.value
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
                }`}
            >
              <span className="text-xl block mb-1">{type.icon}</span>
              <span className="text-xs font-medium">{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="date">Date</Label>
          <Input
            id="date"
            type="date"
            value={value.date}
            onChange={(e) => setField('date', e.target.value)}
            required
            data-testid="event-date-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="time">Heure</Label>
          <Input
            id="time"
            type="time"
            value={value.time}
            onChange={(e) => setField('time', e.target.value)}
            required
            data-testid="event-time-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="location">Lieu</Label>
        <Input
          id="location"
          value={value.location}
          onChange={(e) => setField('location', e.target.value)}
          placeholder="Adresse ou lieu"
          required
          data-testid="event-location-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={value.description}
          onChange={(e) => setField('description', e.target.value)}
          placeholder="Description de l'événement"
          rows={3}
          data-testid="event-description-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="program" className="flex items-center gap-1.5">
          <ListChecks className="h-4 w-4" />
          Programme <span className="text-xs font-normal text-muted-foreground">(optionnel)</span>
        </Label>
        <Textarea
          id="program"
          value={value.program || ''}
          onChange={(e) => setField('program', e.target.value)}
          placeholder={'Une étape par ligne, ex :\n21h00 — Accueil des invités\n22h00 — Récital de Khassidas\n23h30 — Conférence'}
          rows={4}
          data-testid="event-program-input"
        />
        <p className="text-xs text-muted-foreground">Une ligne par étape — visible via « Voir le programme ».</p>
      </div>
    </>
  );
}

// Champs de configuration d'une collecte (réutilisés en création et en édition)
function FundraisingFields({ value, onChange, sassCategories }) {
  const setField = (k, v) => onChange({ ...value, [k]: v });
  const setCat = (catId, v) => onChange({ ...value, amountsByCategory: { ...value.amountsByCategory, [catId]: v } });
  return (
    <div className="space-y-3 rounded-xl border p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HandCoins className="h-4 w-4 text-emerald-600" />
          <Label className="font-medium">Collecte de fonds</Label>
        </div>
        <Switch checked={value.enabled} onCheckedChange={(c) => setField('enabled', c)} />
      </div>
      {value.enabled && (
        <div className="space-y-3 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Budget / objectif (FCFA)</Label>
              <Input type="number" min="0" value={value.budget} onChange={(e) => setField('budget', e.target.value)} placeholder="Optionnel" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Montant par défaut</Label>
              <Input type="number" min="0" value={value.defaultAmount} onChange={(e) => setField('defaultAmount', e.target.value)} placeholder="ex : 5000" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Montant attendu par profil (catégories Sass)</Label>
            {sassCategories.map((cat) => (
              <div key={cat.id} className="flex items-center gap-2">
                <span className="flex-1 truncate text-sm">{cat.label}</span>
                <div className="relative w-32">
                  <Input
                    type="number"
                    min="0"
                    value={value.amountsByCategory[cat.id] ?? ''}
                    onChange={(e) => setCat(cat.id, e.target.value)}
                    placeholder="défaut"
                    className="h-9 pr-7"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">F</span>
                </div>
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">Laissez vide pour appliquer le montant par défaut.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Events() {
  const { isAdmin, isResponsable, user, userData } = useAuth();
  const isStaff = isAdmin || isResponsable;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    program: '',
    date: '',
    time: '',
    location: '',
    type: 'reunion'
  });
  // Événement dont on affiche le programme (dialog partagé).
  const [programEvent, setProgramEvent] = useState(null);
  const [newFundraising, setNewFundraising] = useState(emptyFundraising());
  const [eventToDelete, setEventToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [participating, setParticipating] = useState(null);

  // Édition d'un événement existant
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editFormFundraising, setEditFormFundraising] = useState(emptyFundraising());
  const [updating, setUpdating] = useState(false);

  // Données de collecte
  const [sassCategories, setSassCategories] = useState([]);
  const [members, setMembers] = useState([]);              // staff : tous les membres
  const [confirmedContribs, setConfirmedContribs] = useState([]); // staff: toutes ; membre: les siennes (confirmées)
  const [linkedExpenses, setLinkedExpenses] = useState([]); // staff : dépenses
  // Détail / édition de la collecte
  const [detailEvent, setDetailEvent] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [editFundraising, setEditFundraising] = useState(emptyFundraising());
  const [savingFund, setSavingFund] = useState(false);

  const eventTypes = [
    { value: 'reunion', label: 'Réunion', icon: '👥', color: 'from-blue-500 to-cyan-500' },
    { value: 'gamou', label: 'Gamou', icon: '🌙', color: 'from-emerald-500 to-green-500' },
    { value: 'ziar', label: 'Ziar', icon: '🕌', color: 'from-purple-500 to-violet-500' },
    { value: 'ceremonie', label: 'Cérémonie', icon: '✨', color: 'from-amber-500 to-yellow-500' },
    { value: 'conference', label: 'Conférence', icon: '🎤', color: 'from-cyan-500 to-blue-500' },
    { value: 'autre', label: 'Autre', icon: '📌', color: 'from-gray-500 to-slate-500' }
  ];

  useEffect(() => {
    fetchEvents();
    fetchFundraisingData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEvents = async () => {
    try {
      const eventsQuery = query(collection(db, 'events'), orderBy('date', 'desc'));
      const snapshot = await getDocs(eventsQuery);
      const eventsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setEvents(eventsData);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Erreur lors du chargement des événements');
    } finally {
      setLoading(false);
    }
  };

  // Données nécessaires aux collectes : profils (Sass), membres, cotisations, dépenses.
  const fetchFundraisingData = async () => {
    try {
      // Catégories Sass (profils) — lisibles par tout membre (settings)
      try {
        const sassSnap = await getDoc(doc(db, 'settings', 'general'));
        const cats = sassSnap.exists() ? sassSnap.data().sassCategories : null;
        setSassCategories(cats?.length ? cats : FALLBACK_SASS);
      } catch (e) {
        setSassCategories(FALLBACK_SASS);
      }

      // Cotisations confirmées : staff = toutes ; membre = les siennes (sans index)
      try {
        const raw = isStaff ? await fetchAllContributions() : await fetchUserContributions(user.uid);
        setConfirmedContribs(raw.filter(isConfirmed));
      } catch (e) {
        console.error('Cotisations (collecte):', e);
      }

      // Staff seulement : liste des membres + dépenses (règles : expenses réservées au staff)
      if (isStaff) {
        try {
          const usersSnap = await getDocs(collection(db, 'users'));
          setMembers(usersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (e) { /* ignore */ }
        try {
          const expSnap = await getDocs(collection(db, 'expenses'));
          setLinkedExpenses(expSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((x) => !x.deleted));
        } catch (e) { /* ignore */ }
      }
    } catch (error) {
      console.error('Error fetching fundraising data:', error);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const eventData = {
        ...newEvent,
        date: new Date(`${newEvent.date}T${newEvent.time}`).toISOString(),
        createdAt: serverTimestamp(),
        participants: [],
        fundraising: cleanFundraising(newFundraising)
      };

      const eventRef = await addDoc(collection(db, 'events'), eventData);
      await logAction(user.uid, user.email, 'CREATE_EVENT', `Événement: ${newEvent.title}`, { type: newEvent.type });

      // Notify all members about the new event
      try {
        const usersSnap = await getDocs(collection(db, 'users'));
        const batch = writeBatch(db);
        usersSnap.docs.forEach(userDoc => {
          const notifRef = doc(collection(db, 'notifications'));
          batch.set(notifRef, {
            userId: userDoc.id,
            title: `Nouvel événement : ${newEvent.title}`,
            message: `Un nouvel événement "${newEvent.title}" a été programmé pour le ${new Date(newEvent.date).toLocaleDateString('fr-FR')}.`,
            type: 'event',
            read: false,
            createdAt: serverTimestamp()
          });
        });
        await batch.commit();
      } catch (notifError) {
        console.error('Error sending group notifications:', notifError);
      }

      toast.success('Événement créé avec succès');
      setDialogOpen(false);
      setNewEvent({ title: '', description: '', program: '', date: '', time: '', location: '', type: 'reunion' });
      setNewFundraising(emptyFundraising());
      fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Erreur lors de la création de l\'événement');
    } finally {
      setCreating(false);
    }
  };

  // Ouvre le dialogue d'édition pré-rempli avec l'événement existant.
  const openEditEvent = (event) => {
    const { date, time } = isoToDateTime(event.date);
    setEditingEvent(event);
    setEditForm({
      title: event.title || '',
      description: event.description || '',
      program: event.program || '',
      date,
      time,
      location: event.location || '',
      type: event.type || 'reunion'
    });
    setEditFormFundraising(fundraisingToForm(event));
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    if (!editingEvent || !editForm) return;
    setUpdating(true);

    try {
      const newDateIso = new Date(`${editForm.date}T${editForm.time}`).toISOString();
      const dateChanged = newDateIso !== editingEvent.date;

      const updatedData = {
        title: editForm.title,
        description: editForm.description,
        program: editForm.program || '',
        date: newDateIso,
        location: editForm.location,
        type: editForm.type,
        fundraising: cleanFundraising(editFormFundraising),
        updatedAt: serverTimestamp()
      };

      await updateDoc(doc(db, 'events', editingEvent.id), updatedData);
      await logAction(user.uid, user.email, 'UPDATE_EVENT', `Événement: ${editForm.title}`, { dateChanged });

      // Notifier les membres si la date a changé
      if (dateChanged) {
        try {
          const usersSnap = await getDocs(collection(db, 'users'));
          const batch = writeBatch(db);
          usersSnap.docs.forEach(userDoc => {
            const notifRef = doc(collection(db, 'notifications'));
            batch.set(notifRef, {
              userId: userDoc.id,
              title: `Événement modifié : ${editForm.title}`,
              message: `L'événement "${editForm.title}" a été reporté au ${new Date(newDateIso).toLocaleDateString('fr-FR')} à ${new Date(newDateIso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}.`,
              type: 'event',
              read: false,
              createdAt: serverTimestamp()
            });
          });
          await batch.commit();
        } catch (notifError) {
          console.error('Error sending update notifications:', notifError);
        }
      }

      toast.success('Événement mis à jour avec succès');
      // Mise à jour locale immédiate
      setEvents((prev) => prev.map((ev) => (ev.id === editingEvent.id ? { ...ev, ...updatedData, updatedAt: new Date() } : ev)));
      setEditingEvent(null);
      setEditForm(null);
      setEditFormFundraising(emptyFundraising());
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Erreur lors de la mise à jour de l\'événement');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;
    setDeleting(true);
    try {
      const deletedEvent = eventToDelete;
      await deleteDoc(doc(db, 'events', deletedEvent.id));
      await logAction(user.uid, user.email, 'DELETE_EVENT', `Événement: ${deletedEvent.title}`);
      toast.success('Événement supprimé avec succès');
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
      setEventToDelete(null);
    }
  };

  const handleParticipate = async (event) => {
    if (!user) {
      toast.error('Vous devez être connecté pour participer');
      return;
    }

    const eventId = event.id;
    const userId = user.uid;
    const isParticipating = event.participants?.includes(userId);

    // Mise à jour optimiste de l'interface
    setEvents(prev => prev.map(e => {
      if (e.id === eventId) {
        const newParticipants = isParticipating
          ? (e.participants || []).filter(id => id !== userId)
          : [...(e.participants || []), userId];
        return { ...e, participants: newParticipants };
      }
      return e;
    }));

    setParticipating(eventId);

    try {
      const eventRef = doc(db, 'events', eventId);
      if (isParticipating) {
        await updateDoc(eventRef, {
          participants: arrayRemove(userId)
        });
        toast.success('Participation annulée');
      } else {
        await updateDoc(eventRef, {
          participants: arrayUnion(userId)
        });
        toast.success('Participation confirmée !');
      }
    } catch (error) {
      console.error('Error updating participation:', error);
      toast.error('Erreur lors de la mise à jour');
      // Retour en arrière en cas d'erreur
      fetchEvents();
    } finally {
      setParticipating(null);
    }
  };

  // ---- Admin : éditer la collecte d'un événement ----
  const openEditFundraising = (event) => {
    setEditEvent(event);
    setEditFundraising(fundraisingToForm(event));
  };

  const handleSaveFundraising = async (e) => {
    e.preventDefault();
    if (!editEvent) return;
    setSavingFund(true);
    try {
      await updateDoc(doc(db, 'events', editEvent.id), { fundraising: cleanFundraising(editFundraising) });
      await logAction(user.uid, user.email, 'UPDATE_EVENT_FUNDRAISING', `Collecte: ${editEvent.title}`);
      toast.success('Collecte mise à jour');
      // Met à jour l'événement ouvert en détail le cas échéant
      setEvents((prev) => prev.map((ev) => (ev.id === editEvent.id ? { ...ev, fundraising: cleanFundraising(editFundraising) } : ev)));
      setDetailEvent((d) => (d && d.id === editEvent.id ? { ...d, fundraising: cleanFundraising(editFundraising) } : d));
      setEditEvent(null);
    } catch (err) {
      console.error('Save fundraising:', err);
      toast.error('Erreur lors de la mise à jour de la collecte');
    } finally {
      setSavingFund(false);
    }
  };

  const getEventType = (type) => eventTypes.find(t => t.value === type) || eventTypes[5];

  const now = new Date();
  const upcomingEvents = events.filter(e => new Date(e.date) >= now);
  const pastEvents = events.filter(e => new Date(e.date) < now);

  const stats = {
    total: events.length,
    upcoming: upcomingEvents.length,
    thisMonth: events.filter(e => {
      const d = new Date(e.date);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length
  };

  const EventCard = ({ event, index }) => {
    const eventType = getEventType(event.type);
    const eventDate = new Date(event.date);
    const isUpcoming = eventDate >= now;
    const isToday = eventDate.toDateString() === now.toDateString();

    return (
      <Card
        className={`group relative overflow-hidden transition-all duration-500 hover:shadow-xl hover:-translate-y-1 border-border/50
          ${isToday ? 'ring-2 ring-primary ring-offset-2' : ''}
        `}
        style={{ animationDelay: `${index * 100}ms` }}
      >
        {/* Gradient top bar */}
        <div className={`h-1.5 bg-gradient-to-r ${eventType.color}`} />

        {/* Background decoration */}
        <div className={`absolute -right-10 -top-10 w-32 h-32 bg-gradient-to-br ${eventType.color} opacity-5 rounded-full blur-2xl transition-transform duration-500 group-hover:scale-150`} />

        <CardHeader className="pb-3 relative">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${eventType.color} flex items-center justify-center text-white text-xl shadow-lg`}>
                {eventType.icon}
              </div>
              <div>
                <Badge variant="secondary" className="text-xs mb-1">
                  {eventType.label}
                </Badge>
                {isToday && (
                  <Badge className="ml-2 bg-primary/10 text-primary border-primary/20 text-xs">
                    Aujourd'hui
                  </Badge>
                )}
                <CardTitle className="text-lg leading-tight">{event.title}</CardTitle>
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-primary"
                  onClick={(e) => { e.stopPropagation(); openEditEvent(event); }}
                  data-testid={`edit-event-${event.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={(e) => { e.stopPropagation(); setEventToDelete(event); }}
                  data-testid={`delete-event-${event.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Date & Time - Large format */}
          <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/50">
            <div className="flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-background shadow-sm border">
              <span className="text-xs text-muted-foreground uppercase font-medium">
                {eventDate.toLocaleDateString('fr-FR', { month: 'short' })}
              </span>
              <span className="text-2xl font-bold text-foreground leading-none">
                {eventDate.getDate()}
              </span>
              <span className="text-xs text-muted-foreground">
                {eventDate.toLocaleDateString('fr-FR', { weekday: 'short' })}
              </span>
            </div>
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Clock className="h-4 w-4 text-muted-foreground" />
                {eventDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" />
                <span className="truncate">{event.location}</span>
              </div>
            </div>
          </div>

          {/* Description */}
          {event.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {event.description}
            </p>
          )}

          {/* Programme de l'événement */}
          {event.program?.trim() && (
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-1.5"
              onClick={(e) => { e.stopPropagation(); setProgramEvent(event); }}
              data-testid={`view-program-${event.id}`}
            >
              <ListChecks className="h-4 w-4" /> Voir le programme
            </Button>
          )}

          {/* Collecte */}
          {event.fundraising?.enabled && (() => {
            const budget = event.fundraising.budget || 0;
            const collected = collectedTotal(event.id, confirmedContribs); // staff: global ; membre: le sien
            const pct = budget > 0 ? Math.min(Math.round((collected / budget) * 100), 100) : 0;
            const myExpected = !isStaff ? expectedAmountForMember(event, userData) : 0;
            const myPaid = !isStaff ? memberPaidForEvent(event.id, user?.uid, confirmedContribs) : 0;
            return (
              <div className="space-y-2 rounded-xl border border-emerald-200/60 bg-emerald-50/40 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/10">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                    <HandCoins className="h-4 w-4" /> Collecte
                  </span>
                  {isStaff && budget > 0 && (
                    <span className="text-xs text-muted-foreground">{collected.toLocaleString()} / {budget.toLocaleString()} F</span>
                  )}
                </div>
                {isStaff ? (
                  budget > 0 && <Progress value={pct} className="h-2" />
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Votre part : <strong>{myExpected.toLocaleString()} F</strong> · versé <strong className={myPaid >= myExpected && myExpected > 0 ? 'text-emerald-600' : ''}>{myPaid.toLocaleString()} F</strong>
                  </p>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-1"
                  onClick={(e) => { e.stopPropagation(); setDetailEvent(event); }}
                >
                  {isStaff ? 'Gérer la collecte' : (myExpected > 0 && myPaid >= myExpected ? 'Collecte — à jour ✓' : 'Cotiser pour cet événement')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            );
          })()}

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-border/50">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              <span>{event.participants?.length || 0} participants</span>
            </div>
            {isUpcoming && (
              <Button
                variant={event.participants?.includes(user?.uid) ? "secondary" : "ghost"}
                size="sm"
                className={`gap-1 ${event.participants?.includes(user?.uid) ? 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100' : 'text-primary hover:text-primary'}`}
                onClick={(e) => { e.stopPropagation(); handleParticipate(event); }}
                disabled={participating === event.id}
              >
                {participating === event.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : event.participants?.includes(user?.uid) ? (
                  <>✓ Je participe</>
                ) : (
                  <>Participer <ChevronRight className="h-4 w-4" /></>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 md:space-y-8" data-testid="events-page">

      {/* Hero Header — compact sur mobile (le titre est déjà donné par l'onglet) */}
      <div className="relative overflow-hidden rounded-2xl md:rounded-3xl bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-700 p-3.5 md:p-8 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptMCAwdi02aC02djZoNnptLTYgNmgtNnY2aDZ2LTZ6bTYgMHY2aDZ2LTZoLTZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50" />
        <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl hidden md:block" />
        <div className="absolute -left-10 -bottom-10 w-48 h-48 bg-purple-300/20 rounded-full blur-3xl" />

        <div className="relative z-10 flex flex-col gap-4 md:flex-row md:justify-between md:items-center md:gap-6">
          <div className="space-y-2 md:space-y-3">
            <div className="hidden md:flex items-center gap-2">
              <Calendar className="h-4 w-4 md:h-5 md:w-5 text-purple-200" />
              <span className="text-purple-200 text-xs md:text-sm font-medium">Calendrier du Dahira</span>
            </div>
            <h1 className="text-lg md:text-4xl font-bold tracking-tight">Événements</h1>
            <p className="text-purple-100/80 max-w-md text-sm hidden md:block">Restez informé des activités à venir.</p>
          </div>

          {isAdmin && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-white text-purple-600 hover:bg-white/90 shadow-lg shadow-black/20 gap-2" data-testid="create-event-btn">
                  <Plus className="h-4 w-4" />
                  Nouvel événement
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    Créer un événement
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateEvent} className="space-y-4">
                  <EventFormFields
                    value={newEvent}
                    onChange={setNewEvent}
                    eventTypes={eventTypes}
                  />

                  <FundraisingFields
                    value={newFundraising}
                    onChange={setNewFundraising}
                    sassCategories={sassCategories}
                  />

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700"
                    disabled={creating}
                    data-testid="submit-event-btn"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Création...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Créer l'événement
                      </>
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-2 md:gap-4">
        <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
          <div className="absolute -right-4 -top-4 w-20 h-20 md:w-24 md:h-24 bg-purple-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
          <CardContent className="p-3 md:p-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] md:text-sm text-muted-foreground">Total</p>
                <p className="text-xl md:text-3xl font-bold">{stats.total}</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-lg">
                <CalendarDays className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
          <div className="absolute -right-4 -top-4 w-20 h-20 md:w-24 md:h-24 bg-emerald-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
          <CardContent className="p-3 md:p-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] md:text-sm text-muted-foreground">À venir</p>
                <p className="text-xl md:text-3xl font-bold text-emerald-600">{stats.upcoming}</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg">
                <CalendarCheck className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
          <div className="absolute -right-4 -top-4 w-20 h-20 md:w-24 md:h-24 bg-amber-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
          <CardContent className="p-3 md:p-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] md:text-sm text-muted-foreground">Ce mois</p>
                <p className="text-xl md:text-3xl font-bold text-amber-600">{stats.thisMonth}</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center shadow-lg">
                <Star className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Upcoming/Past */}
      <Tabs defaultValue="upcoming" value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 pb-2 no-scrollbar">
            <TabsList className="bg-muted/50 p-1 inline-flex min-w-max">
              <TabsTrigger value="upcoming" className="gap-1 md:gap-2 data-[state=active]:bg-background data-[state=active]:shadow text-xs md:text-sm px-2 md:px-3">
                <CalendarCheck className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden md:inline">À venir</span> ({upcomingEvents.length})
              </TabsTrigger>
              <TabsTrigger value="past" className="gap-1 md:gap-2 data-[state=active]:bg-background data-[state=active]:shadow text-xs md:text-sm px-2 md:px-3">
                <History className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden md:inline">Passés</span> ({pastEvents.length})
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        <TabsContent value="upcoming" className="m-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                <p className="text-muted-foreground">Chargement des événements...</p>
              </div>
            </div>
          ) : upcomingEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingEvents.map((event, i) => (
                <EventCard key={event.id} event={event} index={i} />
              ))}
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-16 text-center">
                <div className="w-20 h-20 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mx-auto mb-4">
                  <Calendar className="h-10 w-10 text-purple-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Aucun événement à venir</h3>
                <p className="text-muted-foreground mb-6">
                  {isAdmin ? 'Créez votre premier événement pour commencer' : 'Aucun événement planifié pour le moment'}
                </p>
                {isAdmin && (
                  <Button onClick={() => setDialogOpen(true)} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Créer un événement
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="past" className="m-0">
          {pastEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pastEvents.map((event, i) => (
                <EventCard key={event.id} event={event} index={i} />
              ))}
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-16 text-center">
                <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <History className="h-10 w-10 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Aucun événement passé</h3>
                <p className="text-muted-foreground">
                  L'historique de vos événements apparaîtra ici
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Détail / gestion de la collecte */}
      <Dialog open={!!detailEvent} onOpenChange={(o) => !o && setDetailEvent(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailEvent && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <HandCoins className="h-5 w-5 text-emerald-600" />
                  Collecte — {detailEvent.title}
                </DialogTitle>
                <DialogDescription>
                  {new Date(detailEvent.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </DialogDescription>
              </DialogHeader>

              {!detailEvent.fundraising?.enabled ? (
                <div className="space-y-3 py-8 text-center">
                  <Wallet className="mx-auto h-10 w-10 text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground">Aucune collecte n'est activée pour cet événement.</p>
                  {isAdmin && (
                    <Button onClick={() => openEditFundraising(detailEvent)} className="gap-2">
                      <HandCoins className="h-4 w-4" /> Activer une collecte
                    </Button>
                  )}
                </div>
              ) : (() => {
                const budget = detailEvent.fundraising.budget || 0;
                const collected = collectedTotal(detailEvent.id, confirmedContribs);
                const spent = expensesTotal(detailEvent.id, linkedExpenses);
                const pct = budget > 0 ? Math.min(Math.round((collected / budget) * 100), 100) : 0;
                const evExpenses = linkedExpenses.filter((x) => x.eventId === detailEvent.id);
                const rows = isStaff
                  ? members
                      .map((m) => ({ m, expected: expectedAmountForMember(detailEvent, m), paid: memberPaidForEvent(detailEvent.id, m.id, confirmedContribs) }))
                      .filter((r) => r.expected > 0 || r.paid > 0)
                      .sort((a, b) => (a.paid >= a.expected ? 1 : 0) - (b.paid >= b.expected ? 1 : 0))
                  : [];
                const upToDate = rows.filter((r) => r.expected > 0 && r.paid >= r.expected).length;
                const myExpected = expectedAmountForMember(detailEvent, userData);
                const myPaid = memberPaidForEvent(detailEvent.id, user?.uid, confirmedContribs);
                return (
                  <div className="space-y-4">
                    {isStaff ? (
                      <>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                          {[
                            { l: 'Budget', v: budget ? `${budget.toLocaleString()} F` : '—', c: '' },
                            { l: 'Encaissé', v: `${collected.toLocaleString()} F`, c: 'text-emerald-600' },
                            { l: 'Dépenses', v: `${spent.toLocaleString()} F`, c: 'text-red-600' },
                            { l: 'Solde', v: `${(collected - spent).toLocaleString()} F`, c: collected - spent >= 0 ? 'text-emerald-600' : 'text-red-600' }
                          ].map((s) => (
                            <div key={s.l} className="rounded-lg border p-2 text-center">
                              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{s.l}</p>
                              <p className={`text-sm font-bold ${s.c}`}>{s.v}</p>
                            </div>
                          ))}
                        </div>
                        {budget > 0 && <Progress value={pct} className="h-2" />}
                        {isAdmin && (
                          <Button variant="outline" size="sm" className="gap-2" onClick={() => openEditFundraising(detailEvent)}>
                            <Pencil className="h-4 w-4" /> Modifier la collecte
                          </Button>
                        )}
                        <div className="space-y-1.5">
                          <p className="text-sm font-medium">
                            Participation des membres <span className="font-normal text-muted-foreground">({upToDate}/{rows.length} à jour)</span>
                          </p>
                          <div className="max-h-60 divide-y overflow-y-auto rounded-lg border">
                            {rows.length === 0 ? (
                              <p className="p-3 text-xs text-muted-foreground">Aucun montant attendu défini (renseignez les montants par profil).</p>
                            ) : rows.map(({ m, expected, paid }) => {
                              const ok = expected > 0 && paid >= expected;
                              return (
                                <div key={m.id} className="flex items-center justify-between gap-2 p-2 text-sm">
                                  <span className="flex-1 truncate">{m.fullName || m.email}</span>
                                  <span className="whitespace-nowrap text-xs text-muted-foreground">{paid.toLocaleString()} / {expected.toLocaleString()} F</span>
                                  {ok
                                    ? <Badge className="border-emerald-500/20 bg-emerald-500/10 text-emerald-600">À jour</Badge>
                                    : <Badge className="border-amber-500/20 bg-amber-500/10 text-amber-600">Reste {Math.max(expected - paid, 0).toLocaleString()} F</Badge>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <p className="flex items-center gap-1.5 text-sm font-medium"><TrendingDown className="h-4 w-4 text-red-500" />Dépenses liées ({evExpenses.length})</p>
                          {evExpenses.length === 0 ? (
                            <p className="text-xs text-muted-foreground">Aucune dépense liée. Liez-en depuis la page Finances → Dépenses.</p>
                          ) : (
                            <div className="divide-y rounded-lg border">
                              {evExpenses.map((x) => (
                                <div key={x.id} className="flex items-center justify-between gap-2 p-2 text-sm">
                                  <span className="flex-1 truncate">{x.description || 'Dépense'}</span>
                                  <span className="whitespace-nowrap font-medium text-red-600">-{(x.amount || 0).toLocaleString()} F</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="space-y-3">
                        <div className="rounded-xl border p-4">
                          <p className="text-sm text-muted-foreground">Votre participation</p>
                          <p className="text-2xl font-bold">
                            {myPaid.toLocaleString()} <span className="text-base font-normal text-muted-foreground">/ {myExpected.toLocaleString()} F attendus</span>
                          </p>
                          {myExpected > 0 && <Progress value={Math.min(Math.round((myPaid / myExpected) * 100), 100)} className="mt-2 h-2" />}
                        </div>
                        {myExpected > 0 && myPaid >= myExpected ? (
                          <p className="flex items-center gap-1.5 text-sm text-emerald-600"><CheckCircle className="h-4 w-4" />Vous êtes à jour pour cette collecte. Qu'Allah vous récompense !</p>
                        ) : (
                          <Link to={`/dashboard/contributions?event=${detailEvent.id}`} onClick={() => setDetailEvent(null)}>
                            <Button className="w-full gap-2"><Wallet className="h-4 w-4" />Cotiser pour cet événement</Button>
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Édition d'un événement (admin) */}
      <Dialog open={!!editingEvent} onOpenChange={(o) => { if (!o) { setEditingEvent(null); setEditForm(null); } }}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" />
              Modifier l'événement
            </DialogTitle>
            <DialogDescription>
              Mettez à jour les informations. Les membres seront notifiés en cas de changement de date.
            </DialogDescription>
          </DialogHeader>
          {editForm && (
            <form onSubmit={handleUpdateEvent} className="space-y-4">
              <EventFormFields
                value={editForm}
                onChange={setEditForm}
                eventTypes={eventTypes}
              />

              <FundraisingFields
                value={editFormFundraising}
                onChange={setEditFormFundraising}
                sassCategories={sassCategories}
              />

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700"
                disabled={updating}
                data-testid="update-event-btn"
              >
                {updating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enregistrement...</>
                ) : (
                  <><CheckCircle className="h-4 w-4 mr-2" />Enregistrer les modifications</>
                )}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Édition de la collecte (admin) */}
      <Dialog open={!!editEvent} onOpenChange={(o) => !o && setEditEvent(null)}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><HandCoins className="h-5 w-5 text-emerald-600" />Collecte — {editEvent?.title}</DialogTitle>
            <DialogDescription>Configurez l'objectif et les montants attendus par profil.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveFundraising} className="space-y-4">
            <FundraisingFields value={editFundraising} onChange={setEditFundraising} sassCategories={sassCategories} />
            <Button type="submit" className="w-full" disabled={savingFund}>
              {savingFund ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enregistrement...</> : 'Enregistrer'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Programme de l'événement (lecture seule) */}
      <EventProgramDialog
        event={programEvent}
        open={!!programEvent}
        onOpenChange={(o) => !o && setProgramEvent(null)}
      />

      {/* Delete Event Confirmation Modal */}
      <ConfirmDeleteDialog
        open={!!eventToDelete}
        onOpenChange={(open) => !open && setEventToDelete(null)}
        onConfirm={handleDeleteEvent}
        title="Supprimer cet événement ?"
        description="Cette action est irréversible. L'événement et toutes ses informations seront définitivement supprimés."
        itemName={eventToDelete ? `${getEventType(eventToDelete.type).icon} ${eventToDelete.title} - ${new Date(eventToDelete.date).toLocaleDateString('fr-FR')}` : ''}
        confirmText="Supprimer l'événement"
        loading={deleting}
        variant="danger"
      />
    </div>
  );
}
